import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    Chip
} from '@mui/material';
import { Plus, Trash2, SquarePen, Eye, X } from 'lucide-react';
import { getVisibilityPlans, createVisibilityPlan, updateVisibilityPlan, deleteVisibilityPlan, getVisibilityPlanById } from '../../../Services/AddRetreatManagement/RetreatDetailsApi';
import DeleteModal from '../../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../../Components/Common/GlobalSanckbar';


const StatusBadge = ({ isActive }) => (
    <Box sx={{
        display: 'inline-block',
        px: 1.25,
        py: 0.35,
        borderRadius: '4px',
        bgcolor: isActive ? '#E8F5E8' : '#FFEBEE',
        color: isActive ? '#2E7D32' : '#C62828',
        fontSize: '0.7rem',
        fontWeight: 500
    }}>
        {isActive ? "Active" : "Inactive"}
    </Box>
);

const GetVisibilityPlans = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [visibilityPlans, setVisibilityPlans] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        commissionPercentage: '',
        priorityRank: '',
        displayOrder: '1',
        benefits: '',
        isRecommended: false,
        isActive: true
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // View modal states
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    const handleOpenViewModal = async (item) => {
        setSelectedPlan(item);
        setViewModalOpen(true);
        if (item?.id) {
            try {
                setViewLoading(true);
                const response = await getVisibilityPlanById(item.id);
                if (response && (response.success || response.data)) {
                    const data = response.data || response;
                    if (data && typeof data === 'object') {
                        setSelectedPlan(prev => ({ ...prev, ...data }));
                    }
                }
            } catch (err) {
                console.error("Error fetching visibility plan details:", err);
            } finally {
                setViewLoading(false);
            }
        }
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedPlan(null);
        setViewLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const fetchVisibilityPlans = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getVisibilityPlans();
            if (response && response.success) {
                setVisibilityPlans(response.data);
            } else {
                setError("Failed to load visibility plans.");
            }
        } catch (err) {
            setError("Failed to fetch visibility plans. Please try again later.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVisibilityPlans();
    }, [fetchVisibilityPlans]);

    const handleOpenModal = (item = null) => {
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                name: item.name || '',
                commissionPercentage: item.commissionPercentage ?? '',
                priorityRank: item.priorityRank ?? '',
                displayOrder: item.displayOrder !== undefined && item.displayOrder !== null && item.displayOrder !== '' ? String(item.displayOrder) : '1',
                benefits: item.benefits || '',
                isRecommended: item.isRecommended ?? false,
                isActive: item.isActive ?? true
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                name: '',
                commissionPercentage: '',
                priorityRank: '',
                displayOrder: '1',
                benefits: '',
                isRecommended: false,
                isActive: true
            });
        }
        setValidationErrors({});
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({
            name: '',
            commissionPercentage: '',
            priorityRank: '',
            displayOrder: '1',
            benefits: '',
            isRecommended: false,
            isActive: true
        });
        setValidationErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let finalValue = value;
        if (name === 'displayOrder') {
            if (value !== '') {
                const num = parseInt(value, 10);
                if (num > 999) finalValue = 999;
                else if (num < 1) finalValue = 1;
                else finalValue = num;
            }
        } else if (name.includes('Percentage') || name.includes('Rank')) {
            const parsed = parseFloat(value);
            finalValue = isNaN(parsed) ? '' : parsed;
        }

        if (name === 'commissionPercentage' && finalValue !== '') {
            if (finalValue > 100) {
                finalValue = 100;
            } else if (finalValue < 0) {
                finalValue = 0;
            }
        }

        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSwitchChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = "Plan name is required";
        }
        if (!formData.benefits.trim()) {
            errors.benefits = "Benefits are required";
        }
        if (formData.commissionPercentage === '') {
            errors.commissionPercentage = "Commission percentage is required";
        } else if (isNaN(formData.commissionPercentage) || formData.commissionPercentage < 1 || formData.commissionPercentage > 100) {
            errors.commissionPercentage = "Commission must be between 1 and 100";
        }
        if (formData.priorityRank === '') {
            errors.priorityRank = "Priority rank is required";
        } else if (isNaN(formData.priorityRank) || formData.priorityRank < 0) {
            errors.priorityRank = "Priority rank must be a positive number";
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setSubmitting(true);
        setError(null);
        setValidationErrors({});

        const submitData = {
            ...formData,
            commissionPercentage: parseFloat(formData.commissionPercentage),
            priorityRank: parseInt(formData.priorityRank, 10),
            displayOrder: formData.displayOrder !== "" ? Number(formData.displayOrder) : undefined
        };

        try {
            if (isEdit) {
                await updateVisibilityPlan(currentItem.id, submitData);
            } else {
                await createVisibilityPlan(submitData);
            }

            handleCloseModal();
            fetchVisibilityPlans();
            showSnackbar(`Visibility plan ${isEdit ? 'updated' : 'created'} successfully!`, 'success');
        } catch (err) {
            const serverError = err.response?.data;
            const errorMessage = serverError?.message || `Failed to ${isEdit ? 'update' : 'create'} visibility plan`;
            if (serverError && serverError.details) {
                const fieldErrors = {};
                serverError.details.forEach(detail => {
                    const fieldName = detail.field === 'general' ? 'name' : detail.field;
                    fieldErrors[fieldName] = detail.message;
                });
                setValidationErrors(fieldErrors);
            } else {
                setValidationErrors({ name: errorMessage });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteVisibilityPlan(selectedId);
                if (response && response.success) {
                    showSnackbar("Visibility plan deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    fetchVisibilityPlans();
                } else {
                    setDeleteError(response?.message || "Failed to delete visibility plan");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete visibility plan";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#1E293B', fontSize: { xs: '1.25rem', md: '1.4rem' }, fontWeight: 500 }}>
                        Visibility Plans
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation disableFocusRipple disableRipple
                            variant="contained"
                            startIcon={<Plus size={16} />}
                            onClick={() => handleOpenModal()}
                            sx={{
                                bgcolor: '#FFBD65',
                                px: 2.5,
                                py: 0.85,
                                color: '#FFFFFF',
                                borderRadius: '6px',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                boxShadow: 'none',
                            }}
                        >
                            Add Visibility Plan
                        </Button>
                    </PermissionWrapper>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '6px' }} onClose={() => setError(null)}>{error}</Alert>}

                <Paper sx={{ borderRadius: "6px", border: "1px solid #E2E8F0", overflow: "hidden", bgcolor: '#FFFFFF' }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ minWidth: 1050, width: '100%', tableLayout: 'auto' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>S.No</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Plan Name</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Commission (%)</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Priority Rank</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Display Order</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Status</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Recommended</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                    <TableCell align="left" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                    <TableCell align="right" sx={{ py: 1.5, px: 1.5, fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#475569', whiteSpace: 'nowrap' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : visibilityPlans.length > 0 ? (
                                    visibilityPlans.map((item, idx) => (
                                        <TableRow key={item.id} sx={{
                                            '&:last-child td': { borderBottom: 0 }
                                        }}>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {idx + 1}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B' }}>
                                                        {item.name}
                                                    </Typography>
                                                    {item.isRecommended && (
                                                        <Chip
                                                            label="Recommended"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#EFF6FF',
                                                                color: '#1D4ED8',
                                                                fontSize: '0.65rem',
                                                                fontWeight: 500,
                                                                height: 20,
                                                                borderRadius: '4px'
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                {item.commissionPercentage}%
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                {item.priorityRank}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.displayOrder !== undefined && item.displayOrder !== null ? item.displayOrder : '—'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <StatusBadge isActive={item.isActive} />
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: item.isRecommended ? '#1D4ED8' : '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.isRecommended ? 'Yes' : 'No'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.cuName || '-'}
                                            </TableCell>
                                            <TableCell align="left" sx={{ py: 1.25, px: 1.5, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {item.uuName || '-'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="View">
                                                        <IconButton size="small" sx={{ color: '#64748B', p: 0.5 }} onClick={() => handleOpenViewModal(item)}>
                                                            <Eye size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" sx={{ color: '#64748B', p: 0.5 }} onClick={() => handleOpenModal(item)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" sx={{ color: '#EF4444', p: 0.5 }} onClick={() => handleDeleteClick(item.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 6, color: '#64748B', fontSize: '0.75rem' }}>
                                            No visibility plans found.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Dialog
                    open={openModal}
                    onClose={handleCloseModal}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none'
                        }
                    }}
                >
                    <DialogTitle sx={{ fontWeight: 600, color: '#1E293B', fontSize: '1rem', pb: 1 }}>
                        {isEdit ? 'Edit Visibility Plan' : 'Add Visibility Plan'}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2.5} sx={{ mt: 1 }}>
                            <TextField
                                fullWidth
                                label="Plan Name *"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g. Premium"
                                autoFocus
                                size="small"
                                error={!!validationErrors.name}
                                helperText={validationErrors.name}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                }}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Commission Percentage *"
                                        name="commissionPercentage"
                                        type="number"
                                        size="small"
                                        value={formData.commissionPercentage}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 15"
                                        inputProps={{ min: 1, max: 100 }}
                                        error={!!validationErrors.commissionPercentage}
                                        helperText={validationErrors.commissionPercentage}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Priority Rank *"
                                        name="priorityRank"
                                        type="number"
                                        size="small"
                                        value={formData.priorityRank}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 1"
                                        inputProps={{ min: 0 }}
                                        error={!!validationErrors.priorityRank}
                                        helperText={validationErrors.priorityRank}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        fullWidth
                                        label="Display Order"
                                        name="displayOrder"
                                        type="number"
                                        size="small"
                                        value={formData.displayOrder}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 1"
                                        inputProps={{ min: 1, max: 999 }}
                                        error={!!validationErrors.displayOrder}
                                        helperText={validationErrors.displayOrder || "Optional (1-999)"}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                fontSize: '0.825rem',
                                                '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <TextField
                                fullWidth
                                label="Benefits *"
                                name="benefits"
                                value={formData.benefits}
                                onChange={handleInputChange}
                                placeholder="e.g. Higher rank in search, Featured on homepage"
                                multiline
                                rows={3}
                                size="small"
                                error={!!validationErrors.benefits}
                                helperText={validationErrors.benefits}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.825rem',
                                        '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                    },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#FFBD65' }
                                }}
                            />
                            <Stack direction="row" spacing={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isRecommended}
                                            onChange={handleSwitchChange}
                                            name="isRecommended"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                            }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.825rem', color: '#334155', fontWeight: 500 }}>Recommended Plan</Typography>}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={handleSwitchChange}
                                            name="isActive"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFBD65' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFBD65' }
                                            }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.825rem', color: '#334155', fontWeight: 500 }}>Active</Typography>}
                                />
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, borderTop: '1px solid #F1F5F9' }}>
                        <Button
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleCloseModal}
                            sx={{
                                color: '#475569',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                fontWeight: 500
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            disableElevation disableFocusRipple disableRipple
                            onClick={handleSubmit}
                            disabled={submitting}
                            variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                color: '#fff',
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                px: 2.5,
                            }}
                        >
                            {submitting ? <CircularProgress size={18} color="inherit" /> : (isEdit ? 'Update Plan' : 'Create Plan')}
                        </Button>
                    </DialogActions>
                </Dialog>

                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Visibility Plan"
                    description="Are you sure you want to delete this visibility plan? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />

                {/* VIEW VISIBILITY PLAN DETAILS DIALOG */}
                <Dialog
                    open={viewModalOpen}
                    onClose={handleCloseViewModal}
                    maxWidth="sm"
                    fullWidth
                    scroll="paper"
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            border: '1px solid #E2E8F0',
                            boxShadow: 'none',
                            bgcolor: '#FFFFFF'
                        }
                    }}
                >
                    <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                            Visibility Plan Details
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={handleCloseViewModal}
                            sx={{ color: '#64748B' }}
                        >
                            <X size={18} />
                        </IconButton>
                    </Box>

                    <DialogContent sx={{ p: 2.5 }}>
                        {viewLoading && !selectedPlan ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : selectedPlan ? (
                            <Stack spacing={2.5}>
                                {/* Plan Information Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                                        Plan Information
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Plan Name
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedPlan.name || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Commission (%)
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1E293B' }}>
                                                    {selectedPlan.commissionPercentage !== undefined && selectedPlan.commissionPercentage !== null ? `${selectedPlan.commissionPercentage}%` : '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Priority Rank
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedPlan.priorityRank !== undefined && selectedPlan.priorityRank !== null ? selectedPlan.priorityRank : '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Display Order
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedPlan.displayOrder !== undefined && selectedPlan.displayOrder !== null ? selectedPlan.displayOrder : '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.5 }}>
                                                    Status
                                                </Typography>
                                                <StatusBadge isActive={selectedPlan.isActive} />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Recommended
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: selectedPlan.isRecommended ? '#1D4ED8' : '#1E293B' }}>
                                                    {selectedPlan.isRecommended ? 'Yes' : 'No'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>

                                {/* Plan Benefits Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                                        Plan Benefits
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2 }}>
                                        <Typography sx={{ fontSize: '0.85rem', color: '#1E293B', whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                            {selectedPlan.benefits || '-'}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Audit Information Section */}
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.25 }}>
                                        Audit Information
                                    </Typography>
                                    <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Created By
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedPlan.cuName || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Updated By
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {selectedPlan.uuName || '-'}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Created At
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {formatDate(selectedPlan.createdAt)}
                                                </Typography>
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: '#64748B', mb: 0.25 }}>
                                                    Updated At
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {formatDate(selectedPlan.updatedAt)}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </Stack>
                        ) : (
                            <Typography sx={{ color: '#64748B', fontSize: '0.85rem', textAlign: 'center', py: 4 }}>
                                No details available.
                            </Typography>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #E2E8F0' }}>
                        <Button
                            onClick={handleCloseViewModal}
                            variant="outlined"
                            sx={{
                                color: '#1E293B',
                                borderColor: '#E2E8F0',
                                textTransform: 'none',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                borderRadius: '6px',
                                boxShadow: 'none',
                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' }
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </PermissionWrapper>
    );
};

export default GetVisibilityPlans;
